import { type Language } from '../i18n';

interface ChatProgressProps {
  userMessageCount: number;
  maxQuestions?: number;
  language: Language;
}

type Phase = 'understanding' | 'deepDive' | 'conclusion';

function getPhase(count: number): Phase {
  if (count <= 3) return 'understanding';
  if (count <= 7) return 'deepDive';
  return 'conclusion';
}

const phaseLabels: Record<Language, Record<Phase, string>> = {
  it: {
    understanding: 'Comprensione',
    deepDive: 'Approfondimento',
    conclusion: 'Conclusione',
  },
  en: {
    understanding: 'Understanding',
    deepDive: 'Deep Dive',
    conclusion: 'Conclusion',
  },
};

export function ChatProgress({ userMessageCount, maxQuestions = 10, language }: ChatProgressProps) {
  const currentQuestion = Math.min(userMessageCount + 1, maxQuestions);
  const progress = (userMessageCount / maxQuestions) * 100;
  const phase = getPhase(userMessageCount);

  return (
    <div className="chat-progress mb-4">
      {/* Phase indicator */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* Phase dots */}
          <div className="flex items-center gap-1">
            <div
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                phase === 'understanding' ? 'bg-primary scale-125' : 'bg-primary/30'
              }`}
            />
            <div
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                phase === 'deepDive' ? 'bg-primary scale-125' : phase === 'conclusion' ? 'bg-primary/30' : 'bg-muted'
              }`}
            />
            <div
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                phase === 'conclusion' ? 'bg-primary scale-125' : 'bg-muted'
              }`}
            />
          </div>
          <span className="text-xs font-medium text-primary">
            {phaseLabels[language][phase]}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {language === 'it' ? 'Domanda' : 'Question'} {currentQuestion} {language === 'it' ? 'di' : 'of'} ~{maxQuestions}
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
    </div>
  );
}
